import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Snackbar, Alert, Chip,
  Tabs, Tab,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { getEstabelecimentos } from '../services/api';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import EstabelecimentoForm from './EstabelecimentoForm';
import FiscalSettingsPage from './FiscalSettingsPage';
import WhatsAppSettingsPage from './WhatsAppSettingsPage';
import EmailSettingsPage from './EmailSettingsPage';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';

function readPermissoes() {
  try {
    return JSON.parse(localStorage.getItem('permissoes') || 'null');
  } catch {
    return null;
  }
}

// máscaras reaproveitáveis
const onlyDigits = (v) => String(v || '').replace(/\D/g, '');
const fmtCPF = (v) => onlyDigits(v).slice(0,11)
  .replace(/^(\d{3})(\d)/, '$1.$2')
  .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
  .replace(/\.(\d{3})(\d)/, '.$1-$2');
const fmtCNPJ = (v) => onlyDigits(v).slice(0,14)
  .replace(/^(\d{2})(\d)/, '$1.$2')
  .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
  .replace(/\.(\d{3})(\d)/, '.$1/$2')
  .replace(/(\d{4})(\d)/, '$1-$2');
const fmtDoc = (v) => {
  const d = onlyDigits(v);
  if (!d) return '-';
  return d.length <= 11 ? fmtCPF(d) : fmtCNPJ(d);
};

export default function EstabelecimentoList() {
  const permissoes = readPermissoes();
  const podeAlterarParametros =
    permissoes == null || Boolean(permissoes.alteraParametros);
  const [items, setItems] = useState([]);
  const [snack, setSnack] = useState({ open:false, sev:'success', msg:'' });
  const [form, setForm] = useState({ open: false, id: null });
  const [tab, setTab] = useState(0);

  const carregar = useCallback(async () => {
    try {
      const { data } = await getEstabelecimentos();
      const arr = Array.isArray(data) ? data : (data ? [data] : []);
      setItems(arr);
    } catch {
      setSnack({ open:true, sev:'error', msg:'Erro ao carregar estabelecimentos' });
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const { sorted, orderBy, order, requestSort } = useClientSort(items, 'razaoSocial', 'asc');

  const abrirForm = (id = null) => {
    setTab(0);
    setForm({ open: true, id });
  };
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    setSnack({ open: true, sev: 'success', msg });
    carregar();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Estabelecimentos"
        subtitle="Dados cadastrais da empresa usados nos PDVs e documentos fiscais."
      />

      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTh id="razaoSocial" label="Razão Social" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="nomeFantasia" label="Nome Fantasia" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cnpj" label="CPF/CNPJ" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="email" label="Email" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="telefone" label="Telefone" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum estabelecimento
                </TableCell>
              </TableRow>
            ) : sorted.map(e => (
              <TableRow key={e.id} hover sx={{ cursor: 'pointer' }} onClick={() => abrirForm(e.id)}>
                <TableCell data-label="Razão social">{e.razaoSocial || '-'}</TableCell>
                <TableCell data-label="Nome fantasia">{e.nomeFantasia || '-'}</TableCell>
                <TableCell data-label="CPF/CNPJ">{fmtDoc(e.cnpj)}</TableCell>
                <TableCell data-label="Email">{e.email || '-'}</TableCell>
                <TableCell data-label="Telefone">{e.telefone || '-'}</TableCell>
                <TableCell align="right" data-actions="" onClick={(event) => event.stopPropagation()}>
                  <IconButton color="primary" onClick={() => abrirForm(e.id)}><EditIcon/></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {/* Dica sutil quando só existe 1 */}
      {items.length === 1 && (
        <Box sx={{ mt:2 }}>
          <Chip label="Este ambiente utiliza um único estabelecimento" />
        </Box>
      )}

      <EntityFormDialog
        open={form.open}
        title="Editar estabelecimento"
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={fecharForm}
        maxWidth="lg"
        fullHeight
      >
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Dados da empresa" />
          {podeAlterarParametros && (
            <Tab label="Configurações" disabled={!form.id} />
          )}
          {podeAlterarParametros && (
            <Tab label="WhatsApp" disabled={!form.id} />
          )}
          {podeAlterarParametros && (
            <Tab label="E-mail" disabled={!form.id} />
          )}
        </Tabs>

        {tab === 0 && (
          <EstabelecimentoForm
            key={form.id || 'novo'}
            embedded
            estabelecimentoId={form.id}
            onCancel={fecharForm}
            onSaved={aoSalvar}
          />
        )}
        {podeAlterarParametros && tab === 1 && form.id && (
          <FiscalSettingsPage
            embedded
            estabelecimentoId={form.id}
            onSaved={aoSalvar}
          />
        )}
        {podeAlterarParametros && tab === 2 && form.id && (
          <WhatsAppSettingsPage estabelecimentoId={form.id} />
        )}
        {podeAlterarParametros && tab === 3 && form.id && (
          <EmailSettingsPage />
        )}
      </EntityFormDialog>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={()=>setSnack(s=>({...s, open:false}))}
        anchorOrigin={{vertical:'bottom', horizontal:'right'}}>
        <Alert severity={snack.sev} onClose={()=>setSnack(s=>({...s, open:false}))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
